/* @flow */

import type { Customer } from "shop-state/types";

import React, { useContext, useState } from "react";
import { NavLink } from "react-router-dom";
import { logout } from "@crossroads/shop-state/customer";
import { useSendMessage } from "crustate/react";
import cn from "classnames";
import useFormat from "helpers/use-format";
import { getCustomerActiveBalance } from "helpers/points";
import { StoreInfoContext } from "entrypoint/shared";
import UserIcon from "@material-ui/icons/PersonOutlineRounded";
import MoreIcon from "@material-ui/icons/MoreVertRounded";
import CloseIcon from "@material-ui/icons/CloseRounded";
import Button from "components/Button";
import Wrapper from "components/Wrapper";

import styles from "./styles.scss";

type Props = {
  accountUrl?: string,
  className?: string,
  customer: ?Customer,
};

export const CustomerBanner = ({ customer, className }: {
  customer: ?Customer,
  className?: string,
}): React$Node => {
  const { formatPoints } = useFormat();

  if (!customer) {
    return null;
  }

  const customerBalance = getCustomerActiveBalance(customer);
  const formattedBalance = formatPoints(customerBalance);

  return (
    <Wrapper className={cn(styles.customerBanner, className, "awardit-customerBanner")}>
      <div className={styles.bannerCustomerInfo}>
        <p className={styles.bannerName}>{customer.firstname}</p>
        {customer.memberPoints && customer.memberPoints.slab && (
          <p className={cn(styles.bannerTier, styles.slab, "awardit-customerSlab")}>{customer.memberPoints?.slab}</p>
        )}
      </div>
      <p className={styles.bannerPoints}>{formattedBalance}</p>
    </Wrapper>
  );
};

const CustomerBadge = ({ accountUrl, customer, className }: Props): React$Node => {
  const { formatPoints } = useFormat();
  const sendMessage = useSendMessage();
  const [showLogout, setShowLogout] = useState(false);
  const { content: { accountview, appheader } } = useContext(StoreInfoContext);

  const slabImage = (
    customer &&
    customer.memberPoints &&
    customer.memberPoints.slabImage) ? customer.memberPoints.slabImage : null;

  const slabImageBackground =
    appheader.slabImageBackground !== undefined &&
    appheader.slabImageBackground !== null &&
    appheader.slabImageBackground === true;

  if (!customer) {
    return null;
  }

  const customerBalance = getCustomerActiveBalance(customer);
  const formattedBalance = formatPoints(customerBalance);
  const customerInfo = (
    <>
      {customer.memberPoints && customer.memberPoints.slabImage ?
        <div className={cn((slabImageBackground ? styles.slabBackground : styles.slabLarge), "awardit-headerMemberBadge")}>
          <img src={slabImage} />
        </div> : <UserIcon />
      }
      <span className={styles.name}>{customer.firstname}</span>
      {customer.memberPoints && customer.memberPoints.slab && (
        <span className={cn(styles.slab, "awardit-customerSlab")}>{customer.memberPoints?.slab}</span>
      )}
    </>
  );

  return (
    <div className={cn(styles.block, className)}>
      <span className={styles.points}>{formattedBalance}</span>
      <div className={cn(styles.customerInfoBox, "awardit-customerInfoBox")}>
        {accountUrl ?
          <NavLink
            to={accountUrl}
            activeClassName={styles.active}
            className={styles.customerInfo}
          >
            {customerInfo}
          </NavLink> :
          <div className={styles.customerInfo}>
            {customerInfo}
          </div>
        }
        <button
          type="button"
          aria-expanded={showLogout}
          aria-label={showLogout ? "Hide logout" : "Show logout"}
          className={styles.menuButton}
          onClick={() => setShowLogout(!showLogout)}
        >
          <MoreIcon />
        </button>
        <div
          className={cn(styles.logoutOverlay, { [styles.visible]: showLogout })}
          aria-hidden={!showLogout}
        >
          {accountview.logout &&
            <Button
              className={styles.logout}
              variant="link"
              onClick={() => sendMessage(logout())}
            >
              {accountview.logout}
            </Button>
          }
          <button
            type="button"
            aria-label="Hide logout"
            className={styles.menuButton}
            onClick={() => setShowLogout(false)}
          >
            <CloseIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerBadge;
